import { useRef, useEffect, useState } from 'react';

const areas = [
  {
    icon: 'ri-building-2-line',
    title: 'Direito Imobiliário',
    desc: 'Assessoria jurídica em compra, venda e locação de imóveis, regularizações fundiárias, contratos, financiamentos e usucapião.',
  },
  {
    icon: 'ri-links-line',
    title: 'Sucessões',
    desc: 'Inventários judiciais e extrajudiciais, testamentos, planejamento sucessório e arrolamento de bens com agilidade e segurança.',
  },
  {
    icon: 'ri-file-text-line',
    title: 'Atuação Extrajudicial',
    desc: 'Acordos, notificações extrajudiciais, mediações, contratos e negociações para resolver conflitos sem necessidade de processo judicial.',
  },
  {
    icon: 'ri-hospital-line',
    title: 'Direito Previdenciário',
    desc: 'Aposentadorias, benefícios do INSS, pensões, auxílios e revisões de benefícios previdenciários.',
  },
  {
    icon: 'ri-briefcase-line',
    title: 'Direito Trabalhista',
    desc: 'Rescisões contratuais, reclamações trabalhistas, direitos trabalhistas, horas extras, verbas rescisórias e assessoria a empresas.',
  },
  {
    icon: 'ri-landscape-line',
    title: 'Direito Agrário',
    desc: 'Regularização de terras rurais, contratos agrários, questões possessórias e assessoria a produtores rurais e proprietários.',
  },
  {
    icon: 'ri-leaf-line',
    title: 'Direito Ambiental',
    desc: 'Licenciamento ambiental, regularização de passivos ambientais, compliance ambiental e assessoria em questões de preservação.',
  },
];

const totalCards = areas.length + 1; // +1 for CTA card

interface AreaCardProps {
  area: typeof areas[0];
  visible: boolean;
  index: number;
}

function AreaCard({ area, visible, index }: AreaCardProps) {
  return (
    <div
      className={`group bg-[#2C3756] hover:bg-[#344269] rounded-lg p-6 transition-all duration-500 cursor-default border border-white/5 hover:border-[#C8B07A]/30 flex flex-col ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <div className="w-12 h-12 flex items-center justify-center bg-[#1F2A44] group-hover:bg-[#C8B07A] rounded-lg mb-5 transition-colors duration-300">
        <i className={`${area.icon} text-[#C8B07A] group-hover:text-[#1F2A44] text-2xl transition-colors duration-300`}></i>
      </div>
      <h3 className="text-white font-semibold text-base mb-3 leading-snug group-hover:text-[#C8B07A] transition-colors duration-300">
        {area.title}
      </h3>
      <p className="text-white/50 text-xs leading-relaxed mb-5 flex-1">
        {area.desc}
      </p>
      <a
        href="https://wa.me/5567999210516"
        target="_blank"
        rel="nofollow noreferrer"
        className="inline-flex items-center gap-1.5 text-[#C8B07A] hover:text-[#D6C18F] text-xs font-semibold transition-colors duration-300 cursor-pointer whitespace-nowrap"
      >
        <span className="w-4 h-4 flex items-center justify-center">
          <i className="ri-whatsapp-line text-sm"></i>
        </span>
        Falar com especialista
        <span className="w-4 h-4 flex items-center justify-center">
          <i className="ri-arrow-right-line text-xs"></i>
        </span>
      </a>
    </div>
  );
}

function CtaCard({ visible }: { visible: boolean }) {
  return (
    <div
      className={`bg-[#C8B07A] rounded-lg p-6 flex flex-col justify-between transition-all duration-500 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${areas.length * 80}ms` }}
    >
      <div>
        <div className="w-12 h-12 flex items-center justify-center bg-[#1F2A44] rounded-lg mb-5">
          <i className="ri-chat-3-line text-[#C8B07A] text-2xl"></i>
        </div>
        <h3 className="text-[#1F2A44] font-bold text-base mb-3">
          Consulte nosso escritório
        </h3>
        <p className="text-[#1F2A44]/70 text-xs leading-relaxed mb-5">
          Não encontrou sua área? Entre em contato e descubra como podemos ajudá-lo.
        </p>
      </div>
      <a
        href="https://wa.me/5567999210516"
        target="_blank"
        rel="nofollow noreferrer"
        className="inline-flex items-center justify-center gap-2 bg-[#1F2A44] hover:bg-[#2C3756] text-white px-4 py-2.5 rounded-md font-bold text-xs transition-all duration-300 cursor-pointer whitespace-nowrap"
      >
        <span className="w-4 h-4 flex items-center justify-center">
          <i className="ri-whatsapp-line text-base"></i>
        </span>
        Entrar em contato
      </a>
    </div>
  );
}

export default function PracticeAreas() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleScroll = () => {
    if (!carouselRef.current) return;
    const el = carouselRef.current;
    const firstCard = el.querySelector<HTMLElement>('[data-carousel-card]');
    if (!firstCard) return;
    const cardWidth = firstCard.offsetWidth + 16; // card + gap
    const index = Math.round(el.scrollLeft / cardWidth);
    setActiveIndex(Math.min(index, totalCards - 1));
  };

  const scrollToIndex = (index: number) => {
    if (!carouselRef.current) return;
    const el = carouselRef.current;
    const firstCard = el.querySelector<HTMLElement>('[data-carousel-card]');
    if (!firstCard) return;
    const cardWidth = firstCard.offsetWidth + 16;
    el.scrollTo({ left: cardWidth * index, behavior: 'smooth' });
  };

  return (
    <section id="areas" ref={sectionRef} className="py-24 bg-[#1F2A44]">
      <div className="max-w-7xl mx-auto px-4 md:px-10">
        {/* Section Header */}
        <div
          className={`text-center mb-16 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex items-center justify-center mb-4">
            <div className="h-px w-10 bg-[#C8B07A] mr-4"></div>
            <span className="text-[#C8B07A] text-xs font-semibold tracking-[0.3em] uppercase">Especialidades</span>
            <div className="h-px w-10 bg-[#C8B07A] ml-4"></div>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white font-display mb-4">
            Áreas de Atuação
          </h2>
          <p className="text-white/55 max-w-xl mx-auto text-sm leading-relaxed">
            Atuamos com expertise técnica e estratégica em diversas áreas do Direito, oferecendo soluções jurídicas completas e personalizadas.
          </p>
        </div>

        {/* ── MOBILE CAROUSEL (hidden sm+) ── */}
        <div className="sm:hidden">
          <div
            ref={carouselRef}
            onScroll={handleScroll}
            className="flex overflow-x-auto gap-4 pb-4 snap-x snap-mandatory -mx-4 px-4 scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {areas.map((area, index) => (
              <div
                key={area.title}
                data-carousel-card
                className="flex-shrink-0 w-[82vw] snap-start"
              >
                <AreaCard area={area} visible={visible} index={index} />
              </div>
            ))}
            {/* CTA card */}
            <div data-carousel-card className="flex-shrink-0 w-[82vw] snap-start">
              <CtaCard visible={visible} />
            </div>
          </div>

          {/* Dots */}
          <div className="flex items-center justify-center gap-2 mt-5">
            {Array.from({ length: totalCards }).map((_, i) => (
              <button
                key={i}
                onClick={() => scrollToIndex(i)}
                className={`rounded-full transition-all duration-300 cursor-pointer ${
                  activeIndex === i
                    ? 'w-5 h-2 bg-[#C8B07A]'
                    : 'w-2 h-2 bg-white/25 hover:bg-white/50'
                }`}
                aria-label={`Ir para card ${i + 1}`}
              />
            ))}
          </div>
        </div>

        {/* ── DESKTOP GRID (hidden below sm) ── */}
        <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {areas.map((area, index) => (
            <AreaCard key={area.title} area={area} visible={visible} index={index} />
          ))}
          <CtaCard visible={visible} />
        </div>
      </div>
    </section>
  );
}
